import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useSettings } from "@/contexts/SettingsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Save, Upload, RotateCcw } from "lucide-react";
import { toast } from "sonner";

export default function AdminBranding() {
  const { settings, reload, FONT_FAMILIES } = useSettings();
  const [form, setForm] = useState(settings);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setForm(settings); }, [settings]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const onLogoFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500_000) { toast.error("Image too large (max 500KB)"); return; }
    const reader = new FileReader();
    reader.onload = () => set("logo_image", reader.result);
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", form);
      await reload();
      toast.success("Branding saved — applied everywhere");
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };

  const resetDefaults = async () => {
    if (!confirm("Reset all branding to defaults?")) return;
    const defaults = {
      app_name: "AZHD NutriTrack",
      logo_line1: "AZHD", logo_line2: "NUTRITRACK", logo_image: "/azhd-logo.svg",
      primary_color: "#0E9F6E", secondary_color: "#E8F5F1",
      heading_font: "Manrope", body_font: "IBM Plex Sans",
      login_title: "Welcome", login_subtitle: "Sign in to your nutrition account.",
      hero_title: "Personalised nutrition, one meal at a time.",
      hero_subtitle: "Track meals, follow your plan, and stay aligned with your dietitian.",
      footer_text: "By Dt. Dana Shakaa",
      privacy_url: "/privacy", contact_url: "/contact",
    };
    setForm(defaults);
    try {
      await api.put("/admin/settings", defaults);
      await reload();
      toast.success("Reset to defaults");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="admin-branding-page">
      <header>
        <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">Super admin</p>
        <h1 className="text-3xl font-bold mt-1">Branding & theme</h1>
        <p className="text-muted-foreground mt-1">Customise the app's look — changes apply to every user instantly.</p>
      </header>

      {/* Logo */}
      <section className="nt-card p-6 space-y-4">
        <h2 className="font-semibold text-lg">Logo</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Logo line 1 (wordmark)</Label>
            <Input value={form.logo_line1 || ""} onChange={(e) => set("logo_line1", e.target.value)} data-testid="logo-line1-input" />
          </div>
          <div className="space-y-2">
            <Label>Logo line 2 (tagline)</Label>
            <Input value={form.logo_line2 || ""} onChange={(e) => set("logo_line2", e.target.value)} data-testid="logo-line2-input" />
          </div>
        </div>
        <div className="space-y-2">
          <Label>Logo image (optional — overrides the leaf icon)</Label>
          <div className="flex items-center gap-3">
            {form.logo_image && <img src={form.logo_image} alt="logo" className="h-12 w-auto object-contain rounded-md border border-border p-1 bg-white" />}
            <label className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-input bg-background hover:bg-muted cursor-pointer text-sm font-medium">
              <Upload size={14} /> Upload image
              <input type="file" accept="image/*" onChange={onLogoFile} className="hidden" data-testid="logo-upload-input" />
            </label>
            {form.logo_image && <Button type="button" variant="outline" size="sm" onClick={() => set("logo_image", null)} data-testid="logo-remove">Remove</Button>}
          </div>
          <p className="text-xs text-muted-foreground">PNG or SVG, max 500KB. Stored inline.</p>
        </div>
        <div className="space-y-2">
          <Label>App name (browser title)</Label>
          <Input value={form.app_name || ""} onChange={(e) => set("app_name", e.target.value)} data-testid="app-name-input" />
        </div>
      </section>

      {/* Colors */}
      <section className="nt-card p-6 space-y-4">
        <h2 className="font-semibold text-lg">Colors</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Primary</Label>
            <div className="flex items-center gap-2">
              <Input type="color" value={form.primary_color || "#0E9F6E"} onChange={(e) => set("primary_color", e.target.value)} className="w-16 h-12 p-1" data-testid="primary-color-input" />
              <Input value={form.primary_color || ""} onChange={(e) => set("primary_color", e.target.value)} className="font-mono" data-testid="primary-color-hex" />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Secondary / accent (soft)</Label>
            <div className="flex items-center gap-2">
              <Input type="color" value={form.secondary_color || "#E8F5F1"} onChange={(e) => set("secondary_color", e.target.value)} className="w-16 h-12 p-1" data-testid="secondary-color-input" />
              <Input value={form.secondary_color || ""} onChange={(e) => set("secondary_color", e.target.value)} className="font-mono" data-testid="secondary-color-hex" />
            </div>
          </div>
        </div>
      </section>

      {/* Fonts */}
      <section className="nt-card p-6 space-y-4">
        <h2 className="font-semibold text-lg">Fonts</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Heading font</Label>
            <select value={form.heading_font || "Manrope"} onChange={(e) => set("heading_font", e.target.value)} className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm" data-testid="heading-font-select">
              {FONT_FAMILIES.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>
          <div className="space-y-2">
            <Label>Body font</Label>
            <select value={form.body_font || "IBM Plex Sans"} onChange={(e) => set("body_font", e.target.value)} className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm" data-testid="body-font-select">
              {FONT_FAMILIES.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>
        </div>
      </section>

      {/* Copy */}
      <section className="nt-card p-6 space-y-4">
        <h2 className="font-semibold text-lg">Login & footer copy</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Login title</Label>
            <Input value={form.login_title || ""} onChange={(e) => set("login_title", e.target.value)} data-testid="login-title-input" />
          </div>
          <div className="space-y-2">
            <Label>Login subtitle</Label>
            <Input value={form.login_subtitle || ""} onChange={(e) => set("login_subtitle", e.target.value)} data-testid="login-subtitle-input" />
          </div>
          <div className="space-y-2">
            <Label>Hero title</Label>
            <Input value={form.hero_title || ""} onChange={(e) => set("hero_title", e.target.value)} data-testid="hero-title-input" />
          </div>
          <div className="space-y-2">
            <Label>Hero subtitle</Label>
            <Textarea rows={2} value={form.hero_subtitle || ""} onChange={(e) => set("hero_subtitle", e.target.value)} data-testid="hero-subtitle-input" />
          </div>
          <div className="space-y-2">
            <Label>Footer text</Label>
            <Input value={form.footer_text || ""} onChange={(e) => set("footer_text", e.target.value)} data-testid="footer-text-input" />
          </div>
          <div className="space-y-2">
            <Label>Privacy URL</Label>
            <Input value={form.privacy_url || ""} onChange={(e) => set("privacy_url", e.target.value)} data-testid="privacy-url-input" />
          </div>
          <div className="space-y-2">
            <Label>Contact URL</Label>
            <Input value={form.contact_url || ""} onChange={(e) => set("contact_url", e.target.value)} data-testid="contact-url-input" />
          </div>
        </div>
      </section>

      <div className="flex gap-3">
        <Button onClick={save} disabled={saving} className="gap-2" data-testid="save-branding-button">
          <Save size={16} /> {saving ? "Saving…" : "Save branding"}
        </Button>
        <Button variant="outline" onClick={resetDefaults} className="gap-2" data-testid="reset-branding-button">
          <RotateCcw size={16} /> Reset to defaults
        </Button>
      </div>
    </div>
  );
}
